if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        // Main Viewer navigation arrows (bottom-right of product image)
        const prevMediaBtn = this.querySelector('.product__media-nav-btn--prev');
        const nextMediaBtn = this.querySelector('.product__media-nav-btn--next');

        if (prevMediaBtn) {
          prevMediaBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopImmediatePropagation();
            this.navigateGallery(-1);
          });
        }

        if (nextMediaBtn) {
          nextMediaBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopImmediatePropagation();
            this.navigateGallery(1);
          });
        }

        // Sync discount badge on variant change
        if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
          subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (!event.data || !event.data.html) return;
            const newBadge = event.data.html.querySelector('.product__badge-discount');
            const currentBadge = this.querySelector('.product__badge-discount');
            if (newBadge && currentBadge) {
              currentBadge.innerHTML = newBadge.innerHTML;
              currentBadge.style.display = newBadge.style.display;
            }
          });
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      navigateGallery(direction) {
        if (this.elements.thumbnails) {
          const thumbnails = Array.from(this.elements.thumbnails.querySelectorAll('.thumbnail-list__item'));
          if (thumbnails.length > 1) {
            const currentActiveBtn = this.elements.thumbnails.querySelector('.thumbnail-list__item button[aria-current="true"]');
            const currentActiveItem = currentActiveBtn ? currentActiveBtn.closest('.thumbnail-list__item') : thumbnails[0];
            let currentIndex = thumbnails.indexOf(currentActiveItem);
            if (currentIndex === -1) currentIndex = 0;

            let targetIndex = currentIndex + direction;
            if (targetIndex < 0) {
              targetIndex = thumbnails.length - 1;
            } else if (targetIndex >= thumbnails.length) {
              targetIndex = 0;
            }

            const targetThumbnail = thumbnails[targetIndex];
            if (targetThumbnail && targetThumbnail.dataset.target) {
              this.setActiveMedia(targetThumbnail.dataset.target, false);
              return;
            }
          }
        }

        const mediaItems = Array.from(this.elements.viewer.querySelectorAll('.product__media-item'));
        if (mediaItems.length <= 1) return;
        const currentActiveMedia = this.elements.viewer.querySelector('.product__media-item.is-active') || mediaItems[0];
        let curIdx = mediaItems.indexOf(currentActiveMedia);
        if (curIdx === -1) curIdx = 0;
        let tgtIdx = curIdx + direction;
        if (tgtIdx < 0) tgtIdx = mediaItems.length - 1;
        else if (tgtIdx >= mediaItems.length) tgtIdx = 0;
        const targetMedia = mediaItems[tgtIdx];
        if (targetMedia && targetMedia.dataset.mediaId) {
          this.setActiveMedia(targetMedia.dataset.mediaId, false);
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'smooth' });
          } else if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (this.mql.matches) {
          thumbnail.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          return;
        }

        thumbnail.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
